import { useEffect, DependencyList, useRef } from 'react'

export function useDebounceEffect(
  fn: () => void | (() => void) | Promise<void>,
  waitTime: number,
  deps?: DependencyList,
) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cleanupRef = useRef<null | (() => void)>(null);
  
  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // If a previous debounced run produced a cleanup function, run it now.
    // This allows callers to return a cleanup (e.g., aborting in-flight requests),
    // and ensures dependency changes properly cancel previous work.
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    
    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      const ret = fn();
      if (typeof ret === 'function') {
        cleanupRef.current = ret;
      } else {
        cleanupRef.current = null;
      }
      timeoutRef.current = null;
    }, waitTime)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    }
  }, deps)
}
